import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { PageLoader } from "@/layouts/components/PageLoader/PageLoader";
import { trackingService } from "@/lib/tracking";
import { voltAiBot } from "@/assets/images/chat";
import { useUser } from "@/features/user/hooks/useUser";
import { BotMessage } from "../../components/BotMessage";
import { UserMessage } from "../../components/UserMessage";
import { useChatStep } from "../../hooks/useChatStep";
import {
  ChatScrollContext,
  useChatAutoScroll,
} from "../../hooks/useChatAutoScroll";
import { ChatStepId } from "../../types/chat";
import { getChatTurn, getScreenOpenedAtOnce } from "../../turns";
import { ScreenStepTurn } from "../../turns/ScreenStepTurn";
import { WelcomeTurn } from "../../turns/WelcomeTurn";
import { chatCopy, getWelcomeLines } from "../../engine/copy";
import styles from "./ChatPage.module.scss";

// Users who can't go on with the onboarding are sent on without a greeting.
const STEPS_WITHOUT_WELCOME: readonly ChatStepId[] = [
  ChatStepId.ActiveLoan,
  ChatStepId.KycFailed,
];

// The onboarding: every user lands here. Done steps are shown
// as the bot's question and the user's answer; the current step is answered
// in the chat, or opens its screen from a conversation action. Every screen
// hands the user back here once its step is done.
export function ChatPage() {
  const { scenario, facts, profile, step, doneSteps, isLoading } =
    useChatStep();
  const { user } = useUser();
  const location = useLocation();
  // Started from the welcome during this visit; nothing is saved until the
  // first answer, so a new user is greeted again until then.
  const [isStarted, setIsStarted] = useState(false);
  const isReady = !isLoading && !!facts && !!step && !!scenario;
  const { rootRef, contentRef, endRef, scrollToEnd } =
    useChatAutoScroll(isReady);
  const scroll = useMemo(() => ({ scrollToEnd }), [scrollToEnd]);

  const stepId = step?.id;
  // The step shown when the chat opened is history; later ones are typed.
  const firstStepId = useRef(stepId);
  if (!firstStepId.current && stepId) {
    firstStepId.current = stepId;
  }
  const isPromptTyped =
    (!!stepId && stepId !== firstStepId.current) || isStarted;
  // A returning user whose offers expired starts over from the welcome back.
  const showsHistory = firstStepId.current !== ChatStepId.OfferRenewal;
  const isWelcomeShown =
    showsHistory &&
    !isStarted &&
    doneSteps.length === 0 &&
    !!stepId &&
    !STEPS_WITHOUT_WELCOME.includes(stepId);
  const hasWelcomeHistory = showsHistory && (isStarted || doneSteps.length > 0);

  useEffect(() => {
    if (!stepId || !scenario || isWelcomeShown) {
      return;
    }

    void trackingService.trackEvent("chat_step_shown", {
      step_id: stepId,
      scenario_id: scenario.id,
      scenario_version: scenario.version,
    });
  }, [stepId, scenario, isWelcomeShown]);

  if (!isReady) {
    return <PageLoader />;
  }

  const Turn = getChatTurn(step.id) ?? ScreenStepTurn;
  const trackAnswered = () =>
    void trackingService.trackEvent("chat_step_answered", {
      step_id: step.id,
      scenario_id: scenario.id,
      scenario_version: scenario.version,
    });
  const turn = (
    <Turn
      key={step.id}
      scenario={scenario}
      step={step}
      isPromptTyped={isPromptTyped}
      facts={facts}
      profile={profile}
      onAnswered={trackAnswered}
    />
  );

  // A step that opens its screen at once goes there without the chat drawn
  // first: after KYC it would flash on its way to the ANAF screen.
  if (
    !isWelcomeShown &&
    getScreenOpenedAtOnce(
      {
        scenario,
        step,
        isPromptTyped,
        facts,
        profile,
        onAnswered: trackAnswered,
      },
      location.state,
    )
  ) {
    return turn;
  }

  return (
    <div ref={rootRef} className={styles.root}>
      <div ref={contentRef} className={styles.content}>
        <div className={styles.thread}>
          <img src={voltAiBot} alt="Volt AI" className={styles.avatar} />
          {hasWelcomeHistory && (
            <div className={styles.exchange}>
              <BotMessage lines={getWelcomeLines(user?.firstName)} />
              <UserMessage text={chatCopy.welcomeStart} />
            </div>
          )}
          {showsHistory &&
            doneSteps.map((doneStep) => (
              <div key={doneStep.id} className={styles.exchange}>
                <BotMessage lines={doneStep.copy.prompt(facts)} />
                <UserMessage
                  text={
                    doneStep.copy.answer?.(profile) ?? `✓ ${doneStep.copy.done}`
                  }
                />
              </div>
            ))}
        </div>

        <ChatScrollContext.Provider value={scroll}>
          {isWelcomeShown ? (
            <WelcomeTurn
              firstName={user?.firstName}
              onStart={() => setIsStarted(true)}
            />
          ) : (
            turn
          )}
        </ChatScrollContext.Provider>
        {/* After the pinned actions: the newest message ends right above them. */}
        <div ref={endRef} />
      </div>
    </div>
  );
}

export default ChatPage;
